import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { projectId } from '/utils/supabase/info';
import { getBaseUrl } from '../config/app-config';
import { analytics } from '../lib/analytics';
import { supabase } from '../lib/supabase';
import {
  AUTH_ROUTE_CONTEXT_PRESETS,
  enforcePublicRouteIsolation,
  resolveAuthRouteContextFromPreset,
} from '../lib/auth-route-isolation';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function PasswordReset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [hashError, setHashError] = useState<string | null>(null);

  const getPasswordResetRedirectUrl = useCallback((): string => {
    const configuredPublicSite = (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined)?.trim();
    const publicBase = configuredPublicSite || 'https://tensionlabs.net';
    if (import.meta.env.DEV) {
      return `${window.location.origin}/password-reset`;
    }
    // Capacitor apps run on localhost in WebView, but email links must target public HTTPS domain.
    if (Capacitor.isNativePlatform()) {
      return `${publicBase.replace(/\/+$/, '')}/password-reset`;
    }
    const detectedBase = getBaseUrl();
    if (detectedBase.startsWith('http://localhost') || detectedBase.includes('127.0.0.1')) {
      return `${publicBase.replace(/\/+$/, '')}/password-reset`;
    }
    return `${detectedBase.replace(/\/+$/, '')}/password-reset`;
  }, []);
  
  const markRecoveryReady = useCallback((showToast: boolean) => {
    setHashError(null);
    setIsUpdateMode(true);
    if (showToast) {
      toast.success('Email verified! Set your new password below.');
    }
    window.history.replaceState(null, '', `${window.location.pathname}?type=recovery`);
  }, []);

  const markRecoveryError = useCallback((message: string) => {
    setIsUpdateMode(false);
    setHashError(message);
    toast.error(message);
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const getRecoveryContext = useCallback(() => {
    const context = resolveAuthRouteContextFromPreset(
      searchParams,
      AUTH_ROUTE_CONTEXT_PRESETS.passwordRecovery,
    );
    const hashType = context.hashParams.get('type');
    const legacyToken = searchParams.get('token');
    const isRecoveryLink = context.hasContext || !!legacyToken;

    return {
      hashParams: context.hashParams,
      queryType: context.queryType,
      hashType,
      legacyToken,
      cameFromSupabaseAuth: context.cameFromSupabaseAuth,
      isRecoveryLink,
    };
  }, [searchParams]);

  useEffect(() => {
    let mounted = true;

    const enforcePasswordResetIsolation = async () => {
      const { isRecoveryLink } = getRecoveryContext();
      await enforcePublicRouteIsolation(isRecoveryLink);
      if (!mounted) return;
    };

    void enforcePasswordResetIsolation();
    return () => {
      mounted = false;
    };
  }, [getRecoveryContext]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        markRecoveryReady(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [markRecoveryReady]);

  useEffect(() => {
    let mounted = true;

    const checkRecoveryLink = async () => {
      const {
        hashParams,
        queryType,
        hashType,
        legacyToken,
        cameFromSupabaseAuth,
        isRecoveryLink,
      } = getRecoveryContext();

      if (!isRecoveryLink) return;

      // Backward compatibility: some templates send /password-reset?token=... directly.
      // Convert that into Supabase verify flow so the browser gets a valid recovery session.
      if (legacyToken && !searchParams.get('token_hash') && !searchParams.get('code')) {
        const redirectTo = `${getPasswordResetRedirectUrl()}?type=recovery`;
        const verifyUrl = `https://${projectId}.supabase.co/auth/v1/verify?token=${encodeURIComponent(legacyToken)}&type=recovery&redirect_to=${encodeURIComponent(redirectTo)}`;
        window.location.assign(verifyUrl);
        return;
      }

      const hashErrorValue = hashParams.get('error');
      const hashErrorCode = hashParams.get('error_code');
      const hashErrorDescription = hashParams.get('error_description');
      if (hashErrorValue) {
        if (hashErrorCode === 'otp_expired') {
          markRecoveryError('This password reset link has expired. Please request a new one.');
        } else {
          markRecoveryError(hashErrorDescription || 'Invalid or expired reset link.');
        }
        return;
      }

      try {
        const queryTokenHash = searchParams.get('token_hash');
        const queryAccessToken = searchParams.get('access_token');
        const queryRefreshToken = searchParams.get('refresh_token');
        const hashAccessToken = hashParams.get('access_token');
        const hashRefreshToken = hashParams.get('refresh_token');
        const authCode = searchParams.get('code');

        if (queryTokenHash && queryType === 'recovery') {
          const { error } = await supabase.auth.verifyOtp({
            type: 'recovery',
            token_hash: queryTokenHash,
          });
          if (error) throw error;
        } else if (authCode) {
          const { error } = await supabase.auth.exchangeCodeForSession(authCode);
          if (error) throw error;
        } else if ((queryAccessToken && queryRefreshToken) || (hashAccessToken && hashRefreshToken)) {
          const { error } = await supabase.auth.setSession({
            access_token: queryAccessToken ?? hashAccessToken ?? '',
            refresh_token: queryRefreshToken ?? hashRefreshToken ?? '',
          });
          if (error) throw error;
        }

        let {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          // In WebViews, session hydration can be delayed after verify redirect.
          for (let attempt = 0; attempt < 5 && !session; attempt += 1) {
            await new Promise((resolve) => setTimeout(resolve, 400));
            const retry = await supabase.auth.getSession();
            session = retry.data.session;
          }
        }
        if (!mounted) return;
        if (session) {
          markRecoveryReady(true);
        } else if (queryType === 'recovery' || hashType === 'recovery' || cameFromSupabaseAuth) {
          markRecoveryReady(false);
        } else {
          markRecoveryError('Failed to verify reset link. Please request a new one.');
        }
      } catch (err: any) {
        console.error('Password recovery verification error:', err);
        if (!mounted) return;
        markRecoveryError(err?.message || 'Failed to verify reset link. Please request a new one.');
      }
    };

    void checkRecoveryLink();
    return () => {
      mounted = false;
    };
  }, [getRecoveryContext, getPasswordResetRedirectUrl, markRecoveryError, markRecoveryReady]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setHashError(null);

    try {
      // Request reset directly from Supabase.
      // Avoids coupling UX to custom pre-check routes and aligns with secure, non-enumerating auth flows.
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getPasswordResetRedirectUrl(),
      });

      if (error) throw error;

      setEmailSent(true);
      analytics.track('password_reset_requested', { email });
      toast.success('If this email is registered, a reset link has been sent.');
    } catch (error: any) {
      console.error('Password reset request error:', error);
      setHashError(error.message || 'Failed to send reset email. Please try again.');
      toast.error(error.message || 'Failed to send reset email');
      analytics.track('password_reset_failed', { email, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      analytics.track('password_reset_completed');
      toast.success('Password updated successfully!');
      
      // Sign out to require fresh login with new password
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error: any) {
      console.error('Password update error:', error);
      toast.error(error.message || 'Failed to update password');
      analytics.track('password_reset_update_failed', { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen px-6 py-10">
      <div className="mx-auto w-full max-w-md">
        <StockHeroStrip stockKey="coupleHomeRelax" alt="Calm moment — illustrative stock" />
        <Button
          variant="ghost"
          onClick={() => navigate('/auth')}
          className="mb-6"
          aria-label="Back to sign in"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sign In
        </Button>

        <Card className="border-violet-100/90 bg-white/95 p-8 shadow-lg">
          {/* Show error message if hash contained an error */}
          {hashError && (
            <div className="tension-glow mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-2 flex-1">
                  <p className="text-sm font-medium text-red-800">
                    {hashError}
                  </p>
                  {hashError.includes('No account found') ? (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/auth?signup=true')}
                        className="text-blue-700 border-blue-300 hover:bg-blue-50"
                      >
                        Sign Up Now
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setHashError(null);
                          setEmail('');
                        }}
                        className="text-neutral-700 border-neutral-300"
                      >
                        Try Different Email
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setHashError(null);
                        setIsUpdateMode(false);
                        window.history.replaceState(null, '', '/password-reset');
                      }}
                      className="text-red-700 border-red-300"
                    >
                      Request New Reset Link
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {emailSent ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="p-3 rounded-full bg-green-100">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h2 className="text-2xl font-medium text-neutral-900">Check Your Email</h2>
              <p className="text-neutral-800">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              <p className="text-sm text-neutral-700">
                The link will expire in 1 hour. Didn't receive it? Check your spam folder or{' '}
                <button
                  onClick={() => setEmailSent(false)}
                  className="text-purple-800 hover:text-purple-900 underline font-medium"
                >
                  try again
                </button>
                .
              </p>
            </div>
          ) : isUpdateMode && !hashError ? (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-medium text-neutral-900">Set New Password</h2>
                <p className="text-neutral-800 text-sm">
                  Choose a strong password for your account
                </p>
              </div>

              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-neutral-900">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    aria-label="Enter new password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-neutral-900">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    required
                    minLength={8}
                    aria-label="Confirm new password"
                  />
                </div>

                <div className="tension-glow p-3 bg-purple-50 border border-purple-300 rounded-lg">
                  <p className="text-sm text-purple-950">
                    <strong>Password requirements:</strong>
                  </p>
                  <ul className="text-xs text-purple-900 mt-2 space-y-1">
                    <li>• At least 8 characters long</li>
                    <li>• Mix of letters, numbers, and symbols recommended</li>
                  </ul>
                </div>

                <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-medium text-neutral-900">Reset Password</h2>
                <p className="text-neutral-800 text-sm">
                  Enter your email to receive a password reset link
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-neutral-900">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    aria-label="Enter your email address"
                  />
                </div>

                <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <div className="tension-glow p-3 bg-amber-50 border border-amber-300 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-950">
                    If you don't receive an email within a few minutes, check your spam folder or verify
                    that you entered the correct email address.
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
    </WarmMarketingBackdrop>
  );
}